import { getAssetUrl } from 'pwa-kit-react-sdk/ssr/universal/utils'
import { useConfig } from 'msp-integrations'
import {
  objectType,
  markupType,
  schemaType,
  Recipe,
  Product,
  markupToSchema
} from './JsonLd.types'

const getSchemaType = (type: markupType): schemaType => {
  return markupToSchema[type] || 'WebPage'
}

export const getBody = (type: markupType, object?: objectType) => {
  const schemaType = getSchemaType(type)
  if (schemaType === 'CollectionPage' || schemaType === 'SearchResultsPage')
    return {
      '@type': schemaType,
      mainEntity: {
        '@type': 'ItemList',
        itemListElement:
          Array.isArray(object) && object.length
            ? object.map((p, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                item: {
                  '@type': 'Product',
                  ...processObject('Product', p)
                }
              }))
            : []
      }
    }
  else if (schemaType === 'FAQPage')
    return {
      '@type': schemaType,
      mainEntity:
        Array.isArray(object) && object.length
          ? object.map((f) => [
              {
                '@type': 'Question',
                ...processObject(schemaType, f)
              }
            ])
          : []
    }
  else if (schemaType === 'LocalBusiness' || schemaType === 'WebSite')
    return {
      '@type': schemaType,
      ...processObject(type, object)
    }
  else {
    return {
      '@type': schemaType,
      mainEntity: {
        '@type': type,
        ...processObject(type, object)
      }
    }
  }
}

const processObject = (type: markupType, object?: objectType) => {
  const {
    info: { email, tel, url, brand, organization, logo }
  } = useConfig()

  if (type === 'Product' && object && 'price' in object)
    return {
      name: object.name,
      description: object.description,
      image: object.image,
      sku: object.sku,
      url: object.url ? url + object.url : '',
      brand: { '@type': 'Brand', name: brand },
      offers: {
        '@type': 'Offer',
        price: !object.promotionalPrice
          ? object.price
          : object.promotionalPrice,
        priceCurrency: object.currency,
        availability: object.orderable
          ? 'http://schema.org/InStock'
          : 'http://schema.org/OutOfStock',
        url: object.url ? url + object.url : '',
        seller: {
          '@type': 'Organization',
          name: organization
        }
      }
    }
  else if (type === 'Organization')
    return {
      name: brand,
      telephone: tel,
      email: email,
      url: url,
      logo: logo ? url + getAssetUrl(logo) : ''
    }
  else if (type === 'FAQPage' && object && 'answer' in object)
    return {
      name: object.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: object.answer
      }
    }
  else if (type === 'LocalBusiness' && object && 'openingHours' in object)
    return {
      name: object.name,
      address: {
        '@type': 'PostalAddress',
        ...object.address
      },
      geo: {
        '@type': 'GeoCoordinates',
        ...object.geo
      },
      url: object.url,
      image: object.image,
      telephone: object.tel,
      openingHours: object.openingHours?.map((o) => ({
        '@type': 'OpeningHoursSpecification',
        ...o
      }))
    }
  else if (type === 'SearchAction')
    return {
      url: url,
      potentialAction: {
        '@type': type,
        target: {
          '@type': 'EntryPoint',
          urlTemplate: url + '/search?q={search_term_string}'
        },
        'query-input': 'required name=search_term_string'
      }
    }

  return object
}

const timeToIso = (s: number) => {
  const days = Math.floor(s / 1440)
  s = s - days * 1440
  const hours = Math.floor(s / 60)
  s = s - hours * 60

  let dur = 'PT'
  if (days > 0) {
    dur += days + 'D'
  }
  if (hours > 0) {
    dur += hours + 'H'
  }
  dur += s + 'M'

  return dur
}

export const formatRecipe = (obj: any): Recipe => {
  return {
    name: obj.name,
    recipeYield: obj.recipeYield,
    image: obj.image,
    totalTime: timeToIso(obj?.cookTime ? obj?.cookTime : obj?.cookMinimumTime),
    url: obj.canonicalUrl,
    recipeIngredient: obj.ingredients.map((i: any) => i.label as string),
    recipeInstructions: obj.prepSteps.map((s: any) => ({
      '@type': 'HowToStep',
      text: s.label
    }))
  }
}

export const formatProduct = (obj: any): Product => {
  return {
    name: obj.name,
    description: obj.shortDescription,
    image: obj.image,
    sku: obj.id,
    url: obj.slugUrl,
    price: obj.price,
    promotionalPrice: obj.c_priceDiscounted,
    currency: obj.currency || 'EUR',
    orderable:
      obj?.orderable ||
      obj?.c_inventory?.orderable ||
      obj?.inventory?.orderable ||
      false
  }
}
